import { Component, OnDestroy } from '@angular/core';
import { FcmService } from '@app/core/services/fcm.service';
import { select, Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { combineLatest, Subscription, of, firstValueFrom } from 'rxjs';
import { AppState } from '@app/state/app.state';
import { buyOffer, invalidateCache, loadMarketPlaceOffers } from '@app/state/actions/marketplace.actions';
import { loadInventoryItems, createSellingOffer } from '@app/state/actions/inventory.actions';
import { InventoryV2GetTradableInventoryItems$Params } from '@readygg/ng-api/fn/inventory-v-2/inventory-v-2-get-tradable-inventory-items';
import { selectAppId } from '@app/state/selectors/user.selectors';
import { Actions, ofType } from '@ngrx/effects';
import { selectCurrentFcmToken } from '@app/state/selectors/fcm.selectors';
import { Firestore, collection, query, orderBy, where, collectionChanges, DocumentData } from '@angular/fire/firestore';
import { selectCurrentUserId, selectCustomToken } from '@app/state/selectors/auth.selectors';
import { ReadyGGMarketplaceOfferData } from '@readygg/ng-api';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { MarketplaceGetOffers$Params } from '@readygg/ng-api/fn/marketplace/marketplace-get-offers';
import { CurrencyGetUserCurrencies$Params } from '@readygg/ng-api/fn/currency/currency-get-user-currencies';
import { getUserCurrencies } from '@app/state/actions/user.actions';
import { selectAssetByIdFactory } from '@app/state/selectors/marketplace.selectors';

@Component({
  selector: 'app-messaging',
  templateUrl: './messaging.component.html',
})
export class MessagingComponent implements OnDestroy {
  private subscription: Subscription;
  private appIdSubscription: Subscription;
  private tokenSubscription: Subscription;
  private actionsSubscription: Subscription;
  private firestoreSubscription: Subscription | null = null;
  private appId: string | null = null;
  private isFcmInitialized = false;

  private trackedFirestoreOffers: { [key: string]: string } = {};

  constructor(
    private fcmService: FcmService,
    private messageService: MessageService,
    private store: Store<AppState>,
    private actions$: Actions,
    private firestore: Firestore) {
    // Subscribe to the current FCM token and user ID
    this.tokenSubscription = combineLatest([
      this.store.pipe(select(selectCurrentFcmToken)),
      this.store.pipe(select(selectCurrentUserId)),
      this.store.pipe(select(selectCustomToken)),
    ]).subscribe(([token, userId, customToken]) => {
      // Initialize FCM if token is available and not already initialized
      if (token && !this.isFcmInitialized) {
        this.fcmService.initialize();
        this.isFcmInitialized = true;
      }

      console.log('FCM token:', token);
      console.log('User ID:', userId);
      console.log('Custom token length:', customToken?.length);

      // If FCM token is not empty and not null, ignore Firestore subscription changes
      if (token) {
        if (this.firestoreSubscription) {
          console.log('Unsubscribing from Firestore subscription');
          this.firestoreSubscription.unsubscribe();
          this.firestoreSubscription = null;
        }
      } else {
        // If FCM token is null or empty, use Firestore collection changes
        if (!this.firestoreSubscription && userId && customToken) {
          const q = query(
            collection(this.firestore, 'marketplaceOffers'),
            where('ownerId', '==', userId),
            orderBy('updatedAt', 'desc'));

          this.trackedFirestoreOffers = {};

          this.firestoreSubscription = collectionChanges<DocumentData>(q)
            .pipe(
              mergeMap((changes) => changes),
              map(async (change) => {
                const data = change.doc.data() as ReadyGGMarketplaceOfferData;

                switch (change.type) {
                  case 'added':
                    console.log('Added marketplace offer:', data);
                    this.trackedFirestoreOffers[data.id] = data.status;
                    break;

                  case 'modified': {
                    console.log('Modified marketplace offer:', data);

                    if (!this.trackedFirestoreOffers[data.id] ||
                         this.trackedFirestoreOffers[data.id] === data.status) {
                      break;
                    }

                    this.trackedFirestoreOffers[data.id] = data.status;

                    const offerData = await firstValueFrom(this.store.pipe(select(selectAssetByIdFactory({ id: data.id }))));
                    const detailStr = offerData ? `'${offerData.title}' has been successfully sold for ${offerData.prices![0].quantity} Diamonds.` : 'Your item has been sold.';

                    // If the status changes to 'confirmed', trigger messageService.add
                    if (data.status === 'confirmed') {
                      this.messageService.add({
                        severity: 'info',
                        summary: 'Sold!',
                        detail: detailStr,
                      });

                      this.refreshInventory(); // Refresh inventory after notification
                      this.refreshMarketplaceOffers(); // Refresh marketplace offers when data message is received
                      this.refreshUserCurrencies(); // Refresh user currencies when data message is received
                    }
                  }

                    break;

                  case 'removed':
                    console.log('Removed marketplace offer:', data);
                    break;

                  default:
                    break;
                }
              }),
              catchError((error) => {
                console.error('Error fetching marketplace offers:', error);
                return of(null);
              })).subscribe();
        }
      }
    });

    // Wait for buyOffer or createSellingOffer actions if the FCM token is not yet available
    this.actionsSubscription = this.actions$
      .pipe(ofType(buyOffer, createSellingOffer))
      .subscribe(() => {
        if (!this.isFcmInitialized) {
          this.fcmService.initialize();
          this.isFcmInitialized = true;
        }
      });

    // Subscribe to FCM messages
    this.subscription = this.fcmService.message$.subscribe((msg) => {
      if (msg.notification) {
        this.messageService.add({
          severity: 'info',
          summary: msg.notification.title,
          detail: msg.notification.body,
        });

        console.log('On notification message received:', msg);

        if (msg.data) {
          this.refreshLoadedData(msg.data);
        }
      }
    });

    // Subscribe to the current appId
    this.appIdSubscription = this.store.pipe(select(selectAppId)).subscribe((appId) => {
      this.appId = appId;
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.appIdSubscription.unsubscribe();
    this.tokenSubscription.unsubscribe();
    this.actionsSubscription.unsubscribe();

    if (this.firestoreSubscription) {
      this.firestoreSubscription.unsubscribe();
    }
  }

  refreshLoadedData(data: { [key: string]: string }) {
    console.log('Message data:', data);

    if (data['channel'] === 'marketplace.item_sold' || data['channel'] === 'marketplace.item_bought') {
      this.refreshInventory(); // Refresh inventory when data message is received
      this.refreshMarketplaceOffers(); // Refresh marketplace offers when data message is received
      this.refreshUserCurrencies(); // Refresh user currencies when data message is received
    }

    if (data['channel'] === 'currency.currency_added') {
      this.refreshUserCurrencies(); // Refresh user currencies when data message is received
    }
  }

  private refreshMarketplaceOffers() {
    this.store.dispatch(invalidateCache());

    const params: MarketplaceGetOffers$Params = {
      body: {
        limit: 1000,
      },
    };

    if (this.appId) {
      params.body.appIds = [this.appId];
    }

    this.store.dispatch(loadMarketPlaceOffers({ params }));
  }

  private refreshInventory() {
    if (!this.appId) {
      console.warn('AppId is not available; skipping inventory refresh.');
      return;
    }

    const params: InventoryV2GetTradableInventoryItems$Params = {
      body: {
        includeVirtualItemsData: true,
        appIds: [this.appId],
      },
    };

    this.store.dispatch(loadInventoryItems({ params }));
  }

  private refreshUserCurrencies() {
    const params: CurrencyGetUserCurrencies$Params = {
      body: {
        appId: this.appId || '',
      },
    };
    this.store.dispatch(getUserCurrencies({ params }));
  }
}
